import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { FilterList, EventBusy } from '@material-ui/icons';
import { unpackToDate } from '@campfire/hot-date';
import { difference, min, uniqBy } from 'lodash';
import { AvailabilityHeader } from './AvailabilityHeader';
import { SearchField } from '../inputs/SearchField';
import { ActivitySelect } from './ActivitySelect';
import { SessionSelect } from './SessionSelect';
import { DateAndFrequencySelect } from './DateSelect';
import { ActivityFilter, FilterValueType } from './ActivitySelect/ActivityFilter';
import { AvailabilityCenterGetActivities_vm_activities as ActivityType } from '../../global/components/AvailabilityCenter/__generated__/AvailabilityCenterGetActivities';
import { DateSelect } from './DateSelect/DateSelect';
import { OperationSelect } from './OperationSelect';
import { BulkUnavailabilitySelect, UnavailabilityRange } from './BulkUnavailabilitySelect';

enum AvailaibiltyStepEnum {
  SELECT_OPERATION,
  SELECT_ACTIVITY,
  SELECT_SESSIONS,
  SELECT_DATES,
}

export type ValueProps = {
  operation?: 'availability' | 'unavailability';
  activityId?: string;
  sessionIds?: string[];
  dates?: string[];
  unavailabilities?: UnavailabilityRange[];
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionContainer: {
      paddingTop: '20px',
      paddingBottom: '30px',
      paddingRight: '22px',
    },
    toolbarButton: {
      borderRadius: '8px',
    },
    toolbarButtonSelected: {
      backgroundColor: theme.color.grey.neutral200,
      color: 'white',
      '&:hover': {
        opacity: 0.8,
        backgroundColor: theme.color.grey.neutral200,
      },
    },
    actionButton: {
      textTransform: 'capitalize',
    },
  })
);

export type EnrolmentDataType = {
  [activityId: string]: {
    availabilities: string[];
    unavailabilities: string[];
  };
};

interface Props {
  value: ValueProps;
  onChange: (newValue: ValueProps) => void;
  activities: ActivityType[];
  open: boolean;
  onClose: () => void;
  onSubmit: (value: ValueProps) => void;
  fullScreen?: boolean;
  enrolmentData: EnrolmentDataType;
  managerView?: boolean;
  initialOperation?: 'availability' | 'unavailability';
  availabilityRestrictionDays?: number | null;
}

const SUB_HEADERS = [
  'Choose your action',
  'Select an activity',
  'Select relevant sessions',
  'Select dates or frequency schedules',
];

export function AvailabilityCenterDialog({
  value,
  onChange,
  activities,
  onSubmit,
  open,
  onClose,
  fullScreen,
  enrolmentData,
  managerView,
  initialOperation,
  availabilityRestrictionDays,
}: Props) {
  const classes = useStyles();
  const [step, setStep] = React.useState(
    initialOperation === 'unavailability' ? AvailaibiltyStepEnum.SELECT_DATES : AvailaibiltyStepEnum.SELECT_OPERATION
  );

  const [filter, setFilter] = React.useState<FilterValueType>({ program: 'all', day: {} });
  const [searchValue, setSearchValue] = React.useState('');

  // Set the operation in value when we have an initialOperation
  React.useEffect(() => {
    if (initialOperation && !value.operation) {
      onChange({ ...value, operation: initialOperation });
    }
  }, [initialOperation]);
  const handleNext = () => {
    if (step === AvailaibiltyStepEnum.SELECT_DATES) {
      onSubmit(value);
      onClose();
    } else if (step === AvailaibiltyStepEnum.SELECT_OPERATION && value.operation === 'unavailability') {
      // Skip to bulk unavailability date selection
      setStep(AvailaibiltyStepEnum.SELECT_DATES);
    } else {
      setStep(Math.min(step + 1, AvailaibiltyStepEnum.SELECT_DATES));
    }
  };

  const onSelectOperation = (operation: 'availability' | 'unavailability') => {
    onChange({
      ...value,
      operation,
    });
  };

  const onSelectActivity = (selectedActivityId: string) => {
    onChange({
      ...value,
      activityId: selectedActivityId,
    });
  };

  const onSelectSession = (selectedSessionIds: string[]) => {
    onChange({
      ...value,
      sessionIds: selectedSessionIds,
    });
  };

  const onSelectDates = (selectedDates: string[]) => {
    onChange({
      ...value,
      dates: selectedDates,
    });
  };

  const onSelectUnavailabilities = (unavailabilities: UnavailabilityRange[]) => {
    onChange({
      ...value,
      unavailabilities,
    });
  };

  const selectedActivity = activities.find((activity) => activity.activityId === value.activityId);

  const recurrences = React.useMemo(() => {
    switch (selectedActivity?.__typename) {
      case 'VOLUNTEER_RecurringActivityType':
        return selectedActivity.schedule.recurrences;
      default:
        return [];
    }
  }, [selectedActivity]);

  const [showFilter, setShowFilter] = React.useState(false);
  const onToggleFilter = () => setShowFilter(!showFilter);

  const handleCancel = () => {
    onClose();
  };

  const filterActivities = activities
    .filter((activity) => {
      return filter.program === 'all' || activity.program.programId === filter.program;
    })
    .filter((activity) => {
      return (
        Object.values(filter.day).every((selectedDay) => selectedDay) ||
        Object.values(filter.day).every((selectedDay) => !selectedDay) ||
        Boolean(filter.day[unpackToDate(activity.startDate).weekNumber])
      );
    })
    .filter((activity) => activity.name.toLowerCase().includes(searchValue.toLowerCase()));

  const filterSessions = (selectedActivity?.sessions || []).filter((session) =>
    session.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const onChangeFilter = (newValue: FilterValueType) => {
    setFilter({ ...newValue });
  };

  const isNextEnabled = (() => {
    if (step === AvailaibiltyStepEnum.SELECT_OPERATION) {
      return Boolean(value.operation);
    }
    if (step === AvailaibiltyStepEnum.SELECT_ACTIVITY) {
      return selectedActivity;
    }
    if (step === AvailaibiltyStepEnum.SELECT_SESSIONS) {
      return Boolean(value.sessionIds?.length);
    }
    // For unavailability, allow submission if we have unavailabilities OR if explicitly clearing (empty array)
    if (value.operation === 'unavailability') {
      // Allow submit if we have unavailabilities to save, or if we've explicitly set an empty array (clearing all)
      return value.unavailabilities !== undefined;
    }
    return Boolean(value.dates?.length);
  })();

  React.useEffect(() => {
    setSearchValue('');
  }, [step]);

  const programOptions = uniqBy(
    [{ label: 'All programs', value: 'all' }].concat(
      activities.map((activity) => ({ value: activity.program.programId, label: activity.program.name }))
    ),
    'value'
  );

  const onBack = () => {
    if (step === AvailaibiltyStepEnum.SELECT_DATES && value.operation === 'unavailability') {
      // Go back to operation selection for bulk unavailability
      setStep(AvailaibiltyStepEnum.SELECT_OPERATION);
    } else {
      setStep(Math.max(step - 1, 0));
    }
  };
  const occurrences = selectedActivity?.occurrencesBetween || [];

  const selectedActivityId = selectedActivity?.activityId;

  React.useEffect(() => {
    onChange({
      ...value,
      activityId: selectedActivityId,
      dates: [],
      sessionIds: [],
    });
  }, [selectedActivityId]);

  const dateAvailabilities = enrolmentData[selectedActivity?.activityId as string]?.availabilities || [];
  const dateUnavailabilities = enrolmentData[selectedActivity?.activityId as string]?.unavailabilities || [];
  const selectableOccurrences = difference(occurrences, dateAvailabilities);

  const isUnavailabilityMode = step === AvailaibiltyStepEnum.SELECT_DATES && value.operation === 'unavailability';
  const headerText = isUnavailabilityMode ? 'Set Unavailability Periods' : 'Availability Centre';
  const subHeaderText = isUnavailabilityMode
    ? "Define date ranges when you'll be unavailable. This will clear any existing availabilities for these dates and mark you as unavailable instead."
    : SUB_HEADERS[step];
  const headerIcon = isUnavailabilityMode ? (
    <EventBusy style={{ fontSize: '16px', paddingRight: '5px', marginTop: '2px' }} />
  ) : (
    undefined
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth='md'>
      <DialogTitle>
        <AvailabilityHeader header={headerText} subHeader={subHeaderText} icon={headerIcon}>
          <Box display={'flex'} alignItems='center'>
            {[AvailaibiltyStepEnum.SELECT_ACTIVITY, AvailaibiltyStepEnum.SELECT_SESSIONS].includes(step) && (
              <SearchField
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setSearchValue(e.target.value)
                }
              />
            )}
            {step === AvailaibiltyStepEnum.SELECT_ACTIVITY && (
              <IconButton
                className={classNames(classes.toolbarButton, { [classes.toolbarButtonSelected]: showFilter })}
                onClick={onToggleFilter}
              >
                <FilterList />
              </IconButton>
            )}
          </Box>
        </AvailabilityHeader>
        {showFilter && step === AvailaibiltyStepEnum.SELECT_ACTIVITY && (
          <ActivityFilter programOptions={programOptions} value={filter} onChange={onChangeFilter} />
        )}
      </DialogTitle>
      <DialogContent>
        <Box>
          {(() => {
            switch (step) {
              case AvailaibiltyStepEnum.SELECT_OPERATION:
                return <OperationSelect selectedOperation={value.operation} onSelectOperation={onSelectOperation} />;
              case AvailaibiltyStepEnum.SELECT_ACTIVITY:
                return (
                  <ActivitySelect
                    activities={filterActivities}
                    onSelectActivity={onSelectActivity}
                    selectedActivityId={value.activityId}
                    onClose={onClose}
                    managerView={managerView}
                  />
                );
              case AvailaibiltyStepEnum.SELECT_SESSIONS:
                return (
                  <SessionSelect
                    selectedSessionIds={value.sessionIds || []}
                    sessions={filterSessions}
                    onSelectSession={onSelectSession}
                  />
                );
              case AvailaibiltyStepEnum.SELECT_DATES:
                return value.operation === 'unavailability' ? (
                  <BulkUnavailabilitySelect
                    onChange={onSelectUnavailabilities}
                    existingUnavailabilities={value.unavailabilities || []}
                  />
                ) : recurrences.length > 0 ? (
                  <DateAndFrequencySelect
                    startDate={min([selectedActivity?.startDate, selectableOccurrences[0]])}
                    occurrences={selectableOccurrences}
                    recurrences={recurrences}
                    value={value.dates || []}
                    onChange={onSelectDates}
                    key={selectedActivity?.activityId}
                    availabilities={dateAvailabilities}
                    unavailabilities={dateUnavailabilities}
                    availabilityRestrictionDays={availabilityRestrictionDays}
                  />
                ) : (
                  <DateSelect
                    multiple
                    value={value.dates || []}
                    occurrences={selectableOccurrences}
                    availabilities={dateAvailabilities}
                    unavailabilities={dateUnavailabilities}
                    startDate={min([selectedActivity?.startDate, selectableOccurrences[0]])}
                    onChange={onSelectDates}
                    availabilityRestrictionDays={availabilityRestrictionDays}
                  />
                );
              default:
                return null;
            }
          })()}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actionContainer}>
        {step === AvailaibiltyStepEnum.SELECT_OPERATION ? (
          <Button onClick={handleCancel} disableElevation className={classes.actionButton}>
            Cancel
          </Button>
        ) : (
          <Button onClick={onBack} disableElevation className={classes.actionButton}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          disableElevation
          variant='contained'
          color='primary'
          disabled={!isNextEnabled}
          className={classes.actionButton}
        >
          {step === AvailaibiltyStepEnum.SELECT_DATES ? 'Submit' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
